import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
                      || window.navigator.standalone 
                      || document.referrer.includes('android-app://');

    if (isStandalone) {
      return; // Already installed
    }

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show again for 7 days
      }
    }

    // For Android/Chrome
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show after 3 seconds
    if (isIOSDevice && !isStandalone) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('installPromptDismissed', Date.now().toString());
    setShowPrompt(false);
    setShowIOSInstructions(false);
  };

  if (!showPrompt) {
    return null;
  }

  if (showIOSInstructions) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-6 shadow-2xl z-50 animate-slide-up" dir="rtl">
        <button
          onClick={handleDismiss}
          className="absolute top-4 left-4 text-white/80 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Smartphone className="w-8 h-8" />
            <h3 className="text-xl font-bold">تثبيت التطبيق على آيفون</h3>
          </div>
          
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-300">1.</span>
              <span>اضغط على زر المشاركة <span className="inline-block mx-1">⬆️</span> في شريط Safari السفلي</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-300">2.</span>
              <span>اسحب للأسفل وابحث عن "إضافة إلى الشاشة الرئيسية" <span className="inline-block mx-1">➕</span></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-300">3.</span>
              <span>اضغط على "إضافة" في الزاوية العليا اليسرى</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-blue-300">4.</span>
              <span>الآن يمكنك فتح التمكين من الشاشة الرئيسية! 🎉</span>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 shadow-2xl z-50 animate-slide-up" dir="rtl">
      <button
        onClick={handleDismiss}
        className="absolute top-3 left-3 text-white/80 hover:text-white"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="max-w-lg mx-auto flex items-center gap-4">
        <div className="hidden sm:block">
          <Smartphone className="w-12 h-12" />
        </div>
        
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1">ثبّت تطبيق التمكين</h3>
          <p className="text-sm text-white/90">
            {isIOS 
              ? 'أضف التمكين إلى شاشتك الرئيسية للوصول السريع'
              : 'احصل على تجربة تطبيق كاملة مع إشعارات وعمل بدون إنترنت'
            }
          </p>
        </div>
        
        <Button 
          onClick={handleInstallClick}
          className="bg-white text-blue-900 hover:bg-blue-50 flex items-center gap-2 whitespace-nowrap"
        >
          <Download className="w-4 h-4" />
          تثبيت
        </Button>
      </div>
    </div>
  );
};

export default InstallPrompt;
